import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { ActivatedRoute, Router } from '@angular/router';
import * as fromRoot from '../../../../../../../../state/app.state';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Ability, subject } from '@casl/ability';
import { projectDetails } from '../../../../../../core/projects.selectors';
import { Project } from '../../../../../../../../shared/models';
import { ProjectsService } from '../../../../../../core/projects.service';
import { AuthService } from '../../../../../../../../shared/services';
import { UpdateProjectFailure, UpdateProjectRequest } from '../../../../../../core/projects.actions';
import { Actions, ofType } from '@ngrx/effects';
import {
  PROJECT_ADMIN_FLAGS,
  PROJECT_ADMIN_FLAG_SECTIONS,
  ProjectAdminFlag,
  ProjectAdminFlagSection,
  ProjectAdminFlagState,
  buildProjectAdminFlagUpdate,
  isProjectAdminFlagVisible,
  readProjectAdminFlags,
} from '../../../../../../../../shared/helpers/project-admin-flags';

@Component({
    selector: 'app-each-project-settings',
    templateUrl: './project-settings.component.html',
    styleUrls: ['./project-settings.component.scss'],
    standalone: false
})
export class ProjectSettingsComponent implements OnInit, OnDestroy {
  private readonly onDestroy: Subject<void> = new Subject<void>();
  projectDetails: Project;
  projectDetailsPayload: any;
  readonly adminFlags: ProjectAdminFlag[] = PROJECT_ADMIN_FLAGS;
  readonly adminFlagSections: ProjectAdminFlagSection[] = PROJECT_ADMIN_FLAG_SECTIONS;
  adminFlagState: ProjectAdminFlagState = readProjectAdminFlags(null);
  // Set while a toggle save is in flight so a failed update can revert the switch.
  private adminFlagSavePending = false;
  selectedTabId: number = 0;
  selectedTabLabel: string | null = null;

  headersList: any[] = [
    { id: 0, label: 'Accounts', qp: 'accounts', display: true },
    { id: 1, label: 'Contractors', qp: 'contractors', display: true },
    { id: 2, label: 'Labor', qp: 'labor', display: true },
    { id: 3, label: 'Equipment', qp: 'equipment', display: true },
    { id: 4, label: 'Activity type', qp: 'activity-categories', display: true },
    { id: 5, label: 'Bid items', qp: 'bid-items', display: true },
    { id: 6, label: 'Sites', qp: 'sites', display: true },
    { id: 9, label: 'External labs', qp: 'external-labs', display: true },
    { id: 10, label: 'Internal tests', qp: 'internal-tests', display: true },

    { id: 12, label: 'Material tests', qp: 'material-tests', display: true },
    { id: 13, label: 'Project tags', qp: 'project-tags', display: true },
    { id: 14, label: 'Report templates', qp: 'report-templates', display: true },
    { id: 19, label: 'Forms', qp: 'forms', display: true },
    { id: 16, label: 'Billing settings', qp: 'billing-settings', display: true },
    { id: 17, label: 'Design layers', qp: 'map-layers', display: true },
    { id: 15, label: 'Project settings', qp: 'other-settings', display: false },
    { id: 18, label: 'Archive', qp: 'archive', display: false },
    { id: 20, label: 'ArcGIS push', qp: 'arcgis-push', display: false },
  ];

  isOrgAdmin = false;
  canArchive = false;
  canEditProject = false;

  private unsubscribeAbility: (() => void) | null = null;
  // Angular reuses this component instance across same-route :projectId navigations
  // (e.g. project 215's settings -> project 216's settings), so a one-time ngOnInit
  // computation goes stale the moment the resolved project changes. Track the project
  // these permission flags were last computed for so the projectDetails subscription can
  // detect a project switch and recompute.
  private lastProjectId: string | null = null;

  constructor(
    private store: Store<fromRoot.State>,
    private router: Router,
    private route: ActivatedRoute,
    private projectsService: ProjectsService,
    private authService: AuthService,
    private actions$: Actions,
    private ability: Ability,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    const url = this.router.url;
    const path = url.split('?')[0];
    const segments = path.split('/');
    const projectId = segments[2] ?? null;
    this.lastProjectId = projectId;
    this.refreshProjectScopedFlags(projectId);

    // CASL rules can finish syncing after this component initializes, which would
    // otherwise leave the Archive menu entry permanently hidden for sessions whose
    // cached rules predate the feature. Re-check on the ability's own change stream,
    // matching the child's pattern (project-archive.component.ts). Uses lastProjectId
    // (not the projectId const above) so it stays correct even after a project switch.
    this.unsubscribeAbility = this.ability.on('updated', () => {
      this.refreshProjectScopedFlags(this.lastProjectId);
      this.cdRef.detectChanges();
    });

    this.store
      .select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (Object.keys(data).length !== 0) {
          this.projectDetailsPayload = data;
          this.projectDetails = new Project(data);
          this.adminFlagState = readProjectAdminFlags(data);
          this.adminFlagSavePending = false;

          // Same-route navigation between two projects (e.g. 215's settings ->
          // 216's settings) reuses this component, so the resolved project can change
          // out from under the ngOnInit-computed permission flags above. Recompute
          // whenever the store actually resolves a different project.
          if (data?.id && data.id !== this.lastProjectId) {
            this.lastProjectId = data.id;
            this.refreshProjectScopedFlags(data.id);
            this.cdRef.detectChanges();
          }
        }
      });

    this.actions$
      .pipe(ofType(UpdateProjectFailure), takeUntil(this.onDestroy))
      .subscribe(() => {
        if (!this.adminFlagSavePending) {
          return;
        }

        this.adminFlagState = readProjectAdminFlags(this.projectDetailsPayload);
        this.adminFlagSavePending = false;
      });

    this.route.queryParams.pipe(takeUntil(this.onDestroy)).subscribe(qp => {
      if (qp.subTab) {
        const matchedHeader = this.headersList.find(o => o.qp === (qp.subTab === 'overlays' ? 'map-layers' : qp.subTab));
        if (matchedHeader) {
          this.selectedTabId = matchedHeader.id;
          if (matchedHeader.id >= 5) {
            this.selectedTabLabel = matchedHeader.label;
          }
        }
      }
    });
  }

  selectHeader(header): void {
    this.projectsService.closeRightPanel.next();
    this.selectedTabId = header.id;
    this.selectedTabLabel = null;
    this.updateQueryParams(header.qp);
  }

  selectMenu(header): void {
    this.projectsService.closeRightPanel.next();
    this.selectedTabId = header.id;
    this.selectedTabLabel = header.label;
    this.updateQueryParams(header.qp);
  }

  flagsInSection(section: ProjectAdminFlagSection): ProjectAdminFlag[] {
    return this.adminFlags.filter(
      flag => flag.section === section && isProjectAdminFlagVisible(flag, this.adminFlagState),
    );
  }

  toggleAdminFlag(flag: ProjectAdminFlag, event: any): void {
    if (!this.projectDetailsPayload?.id) {
      return;
    }

    const enabled = event?.checked === true;
    this.adminFlagState = { ...this.adminFlagState, [flag.key]: enabled };
    this.adminFlagSavePending = true;

    const { payload, successMessage } = buildProjectAdminFlagUpdate(
      this.projectDetailsPayload,
      flag.key,
      enabled
    );
    this.store.dispatch(UpdateProjectRequest({ payload, successMessage }));
  }

  private refreshProjectScopedFlags(projectId: string | null): void {
    const account = this.authService?.getCurrentUser();
    const roles = account?.roles || [];
    const projectRole = projectId && roles.length > 0
      ? roles.find((r: { project_id?: string }) => r.project_id === projectId)?.role
      : undefined;
    const effectiveRole = projectRole ?? account?.role;
    this.isOrgAdmin = effectiveRole === 'org-admin';
    this.canArchive = this.ability.can('create', subject('ProjectArchive', { project_id: projectId }));
    this.canEditProject = !!projectId
      && this.ability.can('update', subject('Project', { id: projectId, uuid: projectId }));
    this.updateHeaderDisplay();
  }

  private updateHeaderDisplay(): void {
    this.headersList = this.headersList.map(header => {
      if (header.id === 15) {
        return { ...header, display: this.isOrgAdmin };
      }
      if (header.id === 18) {
        return { ...header, display: this.canArchive };
      }
      if (header.id === 20) {
        return { ...header, display: this.canEditProject };
      }
      return header;
    });
  }

  private updateQueryParams(subTab: string): void {
    this.router.navigate([], {
      queryParams: { subTab },
      queryParamsHandling: 'merge',
    });
  }

  ngOnDestroy() {
    this.unsubscribeAbility?.();
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
